import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

// Material UI components
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';

// Services
import { createTechnician, updateTechnician } from '../services/technicianService';
import { getWarehouses } from '../services/warehouseService';

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters'),
  type: Yup.string()
    .required('Type is required')
    .oneOf(['technician', 'installer'], 'Type must be either technician or installer')
});

const TechnicianForm = ({ open, onClose, technician = null, onSuccess }) => {
  const [error, setError] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const isEdit = Boolean(technician);

  // Fetch warehouses when component mounts
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        setLoadingWarehouses(true);
        const warehouseData = await getWarehouses();
        setWarehouses(Array.isArray(warehouseData) ? warehouseData : []);
      } catch (err) {
        console.error('Error fetching warehouses:', err);
        setWarehouses([]);
      } finally {
        setLoadingWarehouses(false);
      }
    };

    if (open) {
      fetchWarehouses();
    }
  }, [open]);

  // Initial form values
  const initialValues = {
    name: technician?.name || '',
    type: technician?.type || 'technician',
    warehouse_id: technician?.warehouse_id || ''
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError(null);

      if (isEdit) {
        // Update existing technician
        await updateTechnician(technician.id, values);
      } else {
        // Create new technician
        await createTechnician(values);
      }

      setSubmitting(false);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error saving technician:', err);
      setError(err.message || 'Failed to save technician');
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {isEdit ? 'Edit Technician' : 'Add New Technician'}
      </DialogTitle>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting
        }) => (
          <Form>
            <DialogContent>
              {error && (
                <Box sx={{ mb: 2 }}>
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                </Box>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="name"
                    label="Name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={
                      touched.name && errors.name
                        ? errors.name
                        : "Enter the full name of the technician or installer"
                    }
                    disabled={isSubmitting}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    error={touched.type && Boolean(errors.type)}
                  >
                    <InputLabel id="type-label">Type</InputLabel>
                    <Field
                      as={Select}
                      name="type"
                      labelId="type-label"
                      label="Type"
                      value={values.type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    >
                      <MenuItem value="technician">Technician</MenuItem>
                      <MenuItem value="installer">Installer</MenuItem>
                    </Field>
                    <FormHelperText>
                      {touched.type && errors.type
                        ? errors.type
                        : "Select whether this person is a technician or installer"
                      }
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="warehouse-label">Warehouse (Optional)</InputLabel>
                    <Field
                      as={Select}
                      name="warehouse_id"
                      labelId="warehouse-label"
                      label="Warehouse (Optional)"
                      value={values.warehouse_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting || loadingWarehouses}
                    >
                      <MenuItem value="">
                        <em>No Warehouse Assigned</em>
                      </MenuItem>
                      {warehouses.map(warehouse => (
                        <MenuItem key={warehouse.id} value={warehouse.id}>
                          {warehouse.name} - {warehouse.location}
                        </MenuItem>
                      ))}
                    </Field>
                    <FormHelperText>
                      Select the warehouse this technician/installer is assigned to
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default TechnicianForm;
